import { useState } from 'react'
import { Loader2, Printer } from 'lucide-react'
import { toast } from 'sonner'
import type { MouseEvent } from 'react'

import type { StockInNotePrintData } from '@/lib/stock-in-note-print'
import { Button } from '@/components/ui/button'
import { useStockInNotePrint } from '@/hooks/use-stock-in-note-print'
import { m } from '@/lib/paraglide/messages'

interface Props<TNote> {
    /** Nguồn phiếu: trả thẳng khi màn hình đã có sẵn (màn chi tiết), hoặc trả
     *  Promise khi phải tải trước (dòng danh sách — API danh sách không trả
     *  dòng hàng). `null` nghĩa là không lấy được phiếu. */
    loadNote: () => TNote | null | Promise<TNote | null>
    toPrintData: (note: TNote) => Omit<StockInNotePrintData, 'company'>
    /** Chỉ hiện biểu tượng — dùng trong ô thao tác của bảng danh sách. */
    iconOnly?: boolean
}

/** Nút in phiếu nhập kho, dùng chung cho màn chi tiết và dòng danh sách của cả
 *  6 kho. Chặn lan sự kiện để bấm nút trong dòng bảng không mở màn chi tiết. */
export function StockInNotePrintButton<TNote>({
    loadNote,
    toPrintData,
    iconOnly = false,
}: Props<TNote>) {
    const { print } = useStockInNotePrint()
    const [loading, setLoading] = useState(false)

    const handleClick = async (event: MouseEvent) => {
        event.stopPropagation()
        if (loading) return
        setLoading(true)
        try {
            const note = await loadNote()
            if (!note) {
                toast.error(m.sinp_load_note_failed())
                return
            }
            print(toPrintData(note))
        } catch {
            toast.error(m.sinp_load_note_failed())
        } finally {
            setLoading(false)
        }
    }

    return (
        <Button
            type="button"
            variant={iconOnly ? 'ghost' : 'outline'}
            size={iconOnly ? 'icon' : 'default'}
            className={iconOnly ? undefined : 'h-11 md:h-9'}
            disabled={loading}
            aria-label={m.sinp_print_btn()}
            title={m.sinp_print_btn()}
            onClick={(event) => void handleClick(event)}
        >
            {loading ? (
                <Loader2 className="size-4 animate-spin" />
            ) : (
                <Printer className="size-4" />
            )}
            {!iconOnly && <span className="ml-1">{m.sinp_print_btn()}</span>}
        </Button>
    )
}
